import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { supabase } from './supabaseClient';

const REDIRECT_URL = 'lilherokids://auth-callback';

/**
 * OAuth natif pour iOS/Android via @capacitor/browser + @capacitor/app
 * Ouvre un in-app browser, puis capture le retour via deep link.
 */
export async function signInWithOAuthNative(
  provider: 'google' | 'apple',
  language: string
): Promise<{ error: Error | null }> {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        skipBrowserRedirect: true,
        redirectTo: REDIRECT_URL,
        queryParams: provider === 'google' ? {
          access_type: 'offline',
          prompt: 'consent',
        } : undefined,
      },
    });

    if (error) throw error;
    if (!data.url) throw new Error('No OAuth URL returned');

    return new Promise(async (resolve) => {
      let resolved = false;

      const urlListener = await App.addListener('appUrlOpen', async ({ url }) => {
        if (!url.startsWith(REDIRECT_URL) || resolved) return;
        resolved = true;

        await Browser.close();
        urlListener.remove();
        browserListener.remove();

        const hashPart = url.includes('#') ? url.split('#')[1] : '';
        const params = new URLSearchParams(hashPart);
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');

        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          resolve({ error: sessionError ? new Error(sessionError.message) : null });
        } else {
          const errorDesc = params.get('error_description') || params.get('error');
          resolve({ error: new Error(errorDesc || 'Aucun token reçu') });
        }
      });

      const browserListener = await Browser.addListener('browserFinished', () => {
        if (resolved) return;
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            urlListener.remove();
            browserListener.remove();
            resolve({ error: new Error('Authentification annulée') });
          }
        }, 1500);
      });

      await Browser.open({ url: data.url, presentationStyle: 'popover' });
    });
  } catch (err: any) {
    return { error: err instanceof Error ? err : new Error(String(err.message || err)) };
  }
}
